import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";

import logo from "../../assets/img/logo.jpg";
import { API_BASE } from "../../config/api";

const buttonStyles = {
  minHeight: 48,
  borderRadius: 2,
  bgcolor: "#171310",
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#302821", boxShadow: "none" },
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_BASE}/password-reset/request`, {
        email: email.trim(),
      });
      navigate(`/restablecer-contrasena?token=${encodeURIComponent(data.token)}`);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "No pudimos procesar la solicitud. Intentá nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        bgcolor: "#f6f3ef",
        py: 4,
        backgroundImage:
          "radial-gradient(circle at 15% 20%, rgba(202, 166, 112, .18), transparent 28%), radial-gradient(circle at 85% 80%, rgba(31, 27, 23, .08), transparent 30%)",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #e9e2da",
            px: { xs: 3, sm: 6 },
            py: { xs: 4, sm: 5 },
            boxShadow: "0 22px 60px rgba(45, 35, 24, .10)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <Box
              component="img"
              src={logo}
              alt="Estudio Isaac"
              sx={{ width: 82, height: 82, borderRadius: "50%", objectFit: "cover" }}
            />
          </Box>

          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                mx: "auto",
                mb: 2,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "#f2ece5",
                color: "#2b241e",
              }}
            >
              <EmailOutlinedIcon />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Recuperá tu contraseña
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Ingresá el correo asociado a tu cuenta para continuar.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              autoFocus
              fullWidth
              required
              type="email"
              label="Correo electrónico"
              placeholder="nombre@correo.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={buttonStyles}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Continuar"}
            </Button>
          </Box>

          <Link
            component={RouterLink}
            to="/iniciar-sesión"
            underline="hover"
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              color: "#4a4037",
              fontWeight: 600,
            }}
          >
            <ArrowBackRoundedIcon fontSize="small" />
            Volver a iniciar sesión
          </Link>
        </Paper>
      </Container>
    </Box>
  );
}
